import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getDateString } from 'helpers/helpers';
import { DateTime } from 'luxon';
const { splitByInsightId } = require('helpers/helpers.js');
import { current } from 'immer';
import { getEfficiancyRangeDataOfType, getRangeDataOfType, getSensorsAvgsCurrData } from 'services/dataService';
import { backdropSlice } from '../gui/backdropSlice';
import { usersSlice } from './usersSlice';

const apiUrl = process.env.REACT_APP_BACKEND_URL;

//#region thunks
/* wtf is a thunk?
 * A thunk is a function that wraps an expression to delay its
 * evaluation. https://redux.js.org/usage/writing-logic-thunks
 * This page is for providing a middleware between functions with an unknown outcome * or extra logic and the sensors dispacth
 */

export const fetchRangeDataOfMeasType = createAsyncThunk(
  'heatmapData/fetchRangeDataOfMeasType',
  async (params, { getState, rejectWithValue }) => {
    try {
      // ✅ Get building_id and device_ids from Redux state
      const state = getState();
      const building_id = state.persistence.selectedFilterObject.building;
      const { floor, area } = state.persistence.selectedFilterObject;
      // const device_ids = state.sensors.filteredDevicesIds;
      const device_ids = params.filteredDevicesIds;

      // ✅ Validate building selection
      if (!building_id || building_id === 'undefined' || building_id === 'all') {
        return rejectWithValue('No specific building selected for heatmap data');
      }

      // ✅ Updated API call with device filtering
      const res = await getRangeDataOfType(
        params.type, // measurementType
        building_id, // building_id from Redux state
        params.timeRange || 3600000, // timeframeInMS
        device_ids, // device_ids from Redux state
        params.displayMode,
        floor,
        area,
      );

      // console.log('✅ HeatMap API response received:', res?.length || 0, 'sensors');
      return { data: res };
    } catch (error) {
      console.error('❌ fetchRangeDataOfMeasType error:', error);
      return rejectWithValue(error.message);
    }
  },
);

// ✅ FIXED: Fetch averages of current data of selected sensors
export const fetchAvgsOfCurrData = createAsyncThunk(
  'heatmapData/fetchAvgsOfCurrData',
  async (params, { getState, rejectWithValue }) => {
    try {
      // Get building_id and device_ids from Redux state
      const state = getState();
      const building_id = state.persistence.selectedFilterObject.building;
      const device_ids = state.sensors.filteredDevicesIds;

      // Validate building selection
      if (!building_id || building_id === 'undefined' || building_id === 'all') {
        return rejectWithValue('No specific building selected for current averages');
      }

      const res = await getSensorsAvgsCurrData(building_id, params.userTimeZone, device_ids);

      // console.log('✅ Current averages API response received:', res);
      return { data: res };
    } catch (error) {
      console.error('❌ fetchAvgsOfCurrData error:', error);
      return rejectWithValue(error.message);
    }
  },
);

export const fetchEfficiancyRangeDataOfType = createAsyncThunk(
  'heatmapData/fetchEfficiancyRangeDataOfType',
  async (params, { getState, rejectWithValue }) => {
    try {
      // Get building_id and device_ids from Redux state
      const state = getState();
      const building_id = state.persistence.selectedFilterObject.building;
      const device_ids = state.sensors.filteredDevicesIds;

      // Validate building selection
      if (!building_id || building_id === 'undefined' || building_id === 'all') {
        return rejectWithValue('No specific building selected for efficiency data');
      }

      const res = await getEfficiancyRangeDataOfType(
        params.forHomePage || false,
        params.type,
        building_id,
        params.datesObj,
        device_ids, // device_ids
      );

      // console.log('✅ Efficiency API response received:', res);
      return { data: res };
    } catch (error) {
      console.error('❌ fetchEfficiancyRangeDataOfType error:', error);
      return rejectWithValue(error.message);
    }
  },
);

//#endregion
const heatmapDataInitialState = {
  devices: [],
  data: {
    sensorsTypeMeasurements: [],
    newData: 0,
    currentAverages: null, // field for current averages
  },
  alerts: [],
  timeRange: { label: 'Today', value: 86400000 },
  insightDatesObj: {
    startDate: DateTime.now().startOf('week').minus({ days: 1 }).toFormat('dd.MM.yyyy'),
    endDate: DateTime.local().weekday === 7 ? getDateString(2) : getDateString(1),
  },
  loading: false,
  currentUser: {},
  insightsByInsightType: [],
};

export const heatmapDataSlice = createSlice({
  name: 'heatmapData',
  initialState: heatmapDataInitialState,
  reducers: {
    clear: state => {
      state.heatmapDataInitialState = heatmapDataInitialState;
    },
    clearData: state => {
      state.data = {
        sensorsTypeMeasurements: [],
        newData: 0,
        currentAverages: null,
      };
      state.timeRange = { label: 'Today', value: 86400000 };
      state.newData = 0;
      state.insightsByInsightType = [];
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setHeatmapTimeRangeValue: (state, action) => {
      const NewTimeRange = {
        label: action.payload.label,
        value: action.payload.value,
      };
      state.timeRange = NewTimeRange;
    },
    setInsightsTimeRangeValue: (state, action) => {
      state.insightDatesObj = action.payload;
    },
    newData: (state, action) => {
      state.data.newData = state.data.newData + 1;
    },
    calcInsights: state => {
      let InsightsBySensorsArr = [];

      let sensorsData = [...current(state.data.sensorsTypeMeasurements)];

      sensorsData.forEach(sensor => {
        const sensorObj = {
          sensor_name: sensor.sensor_name,
          location: sensor.location,
        };
        sensorObj.data = sensor.data.filter(rec => rec.insightId >= 7);
        InsightsBySensorsArr.push(sensorObj);
      });

      const groupedByInsightType = {};

      // Loop through the data array
      InsightsBySensorsArr.forEach(sensor => {
        sensor.data.forEach(insightRecord => {
          const { insightId, unix_time } = insightRecord;

          // If the group for this value doesn't exist, initialize it
          if (!groupedByInsightType[insightId]) {
            groupedByInsightType[insightId] = {
              insightId: insightId,
              data: [],
            };
          }

          // Add the formatted object to the group's data array
          groupedByInsightType[insightId].data.push({
            sensor_name: sensor.sensor_name,
            location: sensor.location,
            unix_time: insightRecord.unix_time,
          });
        });
      });

      const arr = Object.entries(groupedByInsightType).map(([key, value]) => ({
        insightId: key,
        ...value,
      }));

      arr.map(insightData => {
        const sensorCounts = insightData.data.reduce((acc, obj) => {
          acc[obj.sensor_name] = (acc[obj.sensor_name] || 0) + 1;
          return acc;
        }, {});

        const sortedData = insightData.data.sort((a, b) => {
          return sensorCounts[b.sensor_name] - sensorCounts[a.sensor_name];
        });
        insightData.data = sortedData;
      });

      state.insightsByInsightType = arr;
    },
  },
  extraReducers: {
    // fetchRangeDataOfMeasType reducers
    [fetchRangeDataOfMeasType.pending]: (state, action) => {
      state.data.sensorsTypeMeasurements = [];
      state.loading = true;
      // console.log('🔄 HeatMap data loading...');
    },
    [fetchRangeDataOfMeasType.fulfilled]: (state, action) => {
      if (!action.payload) {
        state.loading = false;
        return;
      }

      try {
        // console.log('✅ HeatMap data received:', action.payload.data?.length || 0, 'sensors');

        const newState_data = {
          sensorsTypeMeasurements: action.payload.data || [],
          newData: state.data.newData + 1,
          currentAverages: state.data.currentAverages, // ✅ Preserve existing averages
        };

        state.data = newState_data;
        state.loading = false;
      } catch (err) {
        console.error('❌ Error processing HeatMap data:', err);
        state.loading = false;
      }
    },
    [fetchRangeDataOfMeasType.rejected]: (state, action) => {
      console.error('❌ HeatMap data fetch failed:', action.payload); // ✅ Fixed: use action.payload
      state.loading = false;
      state.data.sensorsTypeMeasurements = [];
    },

    // Complete fetchAvgsOfCurrData reducers
    [fetchAvgsOfCurrData.pending]: state => {
      state.loading = true;
    },
    [fetchAvgsOfCurrData.fulfilled]: (state, action) => {
      if (!action.payload) {
        state.loading = false;
        return;
      }
      try {
        const newState_data = {
          sensorsTypeMeasurements: state.data.sensorsTypeMeasurements,
          newData: state.data.newData + 1,
          currentAverages: action.payload.data, // Store the averages data
        };
        state.data = newState_data;
        state.loading = false;
      } catch (err) {
        console.error('❌ Error processing current averages data:', err);
        state.loading = false;
      }
    },
    [fetchAvgsOfCurrData.rejected]: (state, action) => {
      console.error('❌ Current averages fetch failed:', action.payload);
      state.loading = false;
    },

    // ✅ Enhanced efficiency data handling
    [fetchEfficiancyRangeDataOfType.pending]: state => {
      state.loading = true;
    },
    [fetchEfficiancyRangeDataOfType.fulfilled]: (state, action) => {
      if (!action.payload) {
        state.loading = false;
        return;
      }

      // "no data" messages gracefully
      if (
        action.payload.data?.message?.includes('No data') ||
        action.payload.data?.message?.includes('No sensors found')
      ) {
        // console.log('📋 No efficiency data available for current filters');
        const newState_data = {
          sensorsTypeMeasurements: [],
          newData: state.data.newData + 1,
          currentAverages: state.data.currentAverages, // Preserve existing averages
        };
        state.data = newState_data;
        state.loading = false;
        return;
      }

      try {
        // console.log('Efficiency data received:', action.payload.data?.length || 0, 'sensors');

        // Handle both array and single object responses
        const dataArray = Array.isArray(action.payload.data) ? action.payload.data : [action.payload.data];

        state.data = {
          sensorsTypeMeasurements: dataArray.sort((a, b) => (b.data?.length || 0) - (a.data?.length || 0)),
          newData: state.data.newData + 1,
          currentAverages: state.data.currentAverages, // Preserve existing averages
        };
        state.loading = false;
      } catch (err) {
        console.error('❌ Error processing efficiency data:', err);
        state.loading = false;
      }
    },
    [fetchEfficiancyRangeDataOfType.rejected]: (state, action) => {
      console.error('❌ Efficiency data fetch failed:', action.payload);
      state.loading = false;
      state.data.sensorsTypeMeasurements = [];
    },
  },
});

// export heatmapData actions
export const {
  clear: clearSensors,
  clearData,
  setHeatmapTimeRangeValue,
  setInsightsTimeRangeValue,
  setUserToken,
  setLoading,
  calcInsights,
} = heatmapDataSlice.actions;

// utility exports
export const { clear: clearUsers } = usersSlice.actions;
export const { showBackdrop, hideBackdrop } = backdropSlice.actions;

export default backdropSlice.reducer;
